import { useState, useReducer, useRef, useEffect, useCallback } from "react";
import {
    Stack,
    TextField,
    ActionButton,
    Callout,
    DirectionalHint,
    TooltipHost,
    Checkbox,
    PrimaryButton,
    IDetailsHeaderProps,
    ThemeProvider,
    DetailsListLayoutMode
} from "@fluentui/react";
import { Button } from "@fluentui/react-components";
import { Send28Filled, Stop24Filled, List28Regular, PanelLeftExpand24Regular } from "@fluentui/react-icons";
import { DetailsList, Selection, SelectionMode, IColumn } from "@fluentui/react";
import { GoMultiSelect } from "react-icons/go";
import styles from "./QuestionInput.module.css";
import { ClearChatButton } from "../../components/ClearChatButton";
import { TogglePanelButton } from "../../components/TogglePanelButton";
import ChatControls from "../../components/ChatControls/ChatControls";
import { AddURLs } from "../../components/AddURLs";
import { onRenderDetailsHeader } from "../../components/detailedList/detailedMdls";
import { gselectedgroup, gtiers, gloggedinuser, gqasimplified, gfileuserselect } from "../../interfaces";
import {
    actionButtonStyles,
    clipIcon,
    questionInputUploadCallout,
    questionInputTextArea,
    searchTextFieldInCalloutStyle,
    tooltipStylesThumbInScopeChooser
} from "../../styles_glob";

import SpeechRecognizer from "../../components/SpeechRecognizer/SpeechRecognizer";
import { adjustHeight } from "../../util_glob";
import customTheme from "./../../theme/customTheme";

interface Props {
    clearOnSend?: boolean;
    isStreaming: boolean;
    disabled: boolean;
    placeholder?: string;
    permissionLevel: number;
    selectedGroup: gselectedgroup;
    lTiers: gtiers[];
    msgLmtHit: string;
    loggedInUser: gloggedinuser;
    onSend: (
        question: string,
        q_seq_id: number,
        current_intent: string,
        ignore_discard?: boolean,
        additional_string_data?: string,
        msgovrride?: string
    ) => void;
    updateCatsState: (question: string) => void;
    isCancellingMidway: () => void;
    toggleLeftPanel: () => void;
    newConverson: () => void;
    listRefreshRequest: (fileids?: string) => void;
    isOnCallback: (isOn: boolean) => void;
    convoEnd: (question_and_answers_list: gqasimplified[], convokey: string) => void;
    isSpeaking?: boolean;
    isSpeechPaused: boolean | null;
    lastMessage: string[];
    activeConvoKey: string;
    question_and_answers_list: gqasimplified[];
    convo_discard_stamp: number;
    fileselection: gfileuserselect[];
    handleSelectedFids: (fids: gfileuserselect[]) => void;
    onCitationClicked: (filePath: string, integratedCitation: string | null) => void;
}

export const QuestionInput = ({
    clearOnSend,
    isStreaming,
    disabled,
    placeholder,
    permissionLevel,
    selectedGroup,
    lTiers,
    msgLmtHit,
    loggedInUser,
    onSend,
    updateCatsState,
    isCancellingMidway,
    toggleLeftPanel,
    newConverson,
    listRefreshRequest,
    isOnCallback,
    convoEnd,
    isSpeaking,
    isSpeechPaused,
    lastMessage,
    activeConvoKey,
    question_and_answers_list,
    convo_discard_stamp,
    fileselection,
    handleSelectedFids,
    onCitationClicked
}: Props) => {
    const [chatControlsOpen, setChatControlsOpen] = useState<boolean>(false);
    const [isFileUploadDiagOpen, setIsFileUploadDiagOpen] = useState<boolean>(false);
    const [isFileMultiSelectCalloutOpen, setIsFileMultiSelectCalloutOpen] = useState<boolean>(false);
    const [iterQ, SetIterQ] = useState<number>(0);
    const [isSpeakingState, setIsSpeakingState] = useState<boolean>(isSpeaking || false);
    const [isOn, setIsOn] = useState<boolean>(false);
    const [fileMultiSelect, setFileMultiSelect] = useState<gfileuserselect[]>(fileselection ? fileselection.map(f => ({ ...f })) : []);
    const [sortConfig, setSortConfig] = useState<{ key: string; isSortedDescending: boolean }>({ key: "name", isSortedDescending: false });
    const [searchTerm, setSearchTerm] = useState<string>(""); // New state for search
    const qRef = useRef<string>("");
    const convRef = useRef<string>("");
    const textAreaRef = useRef(null);
    const uploadButRef = useRef(null);
    const sendButRef = useRef(null);
    const convDivRef = useRef(null);
    const butContainerRef = useRef(null);
    const q_sequential_ref = useRef<number>(0);
    const ignore_discard_ref = useRef<boolean>(false);
    const additional_instr = useRef<string>("");
    const msgovrride_ref = useRef<string>("");
    const intent_ref = useRef<string>("");
    const [, forceRender] = useReducer(x => x + 1, 0);
    const fileMultiSelectButRef = useRef<HTMLDivElement>(null);

    // This ref will help us prevent the infinite loop
    const isProgrammaticSelectionUpdate = useRef(false);

    const fileColumns: IColumn[] = [
        {
            key: "name",
            name: "Name",
            fieldName: "name",
            minWidth: 120,
            maxWidth: 300,
            isResizable: true,
            isPadded: false,
            onColumnClick: (event: React.MouseEvent<HTMLElement>, column: IColumn) => onColumnClick(event, column)
        },
        {
            key: "doc_type",
            name: "Type",
            fieldName: "doc_category",
            minWidth: 60,
            maxWidth: 120,
            isPadded: false,
            isResizable: true,

            onColumnClick: (event: React.MouseEvent<HTMLElement>, column: IColumn) => onColumnClick(event, column)
        },
        {
            key: "modified",
            name: "Modified",
            fieldName: "modified",
            minWidth: 60,
            maxWidth: 120,
            isPadded: false,
            isResizable: true,
            onRender: (item: gfileuserselect) => {
                return <span>{item.modified ? new Date(item.modified).toLocaleDateString() : "N/A" /* Format as needed */}</span>;
            },
            onColumnClick: (event: React.MouseEvent<HTMLElement>, column: IColumn) => onColumnClick(event, column)
        }
    ];

    const selectionRef = useRef<Selection>();
    if (!selectionRef.current) {
        selectionRef.current = new Selection({
            selectionMode: SelectionMode.multiple,
            onSelectionChanged: () => {
                // IMPORTANT: Only update if the change was NOT programmatic
                if (!isProgrammaticSelectionUpdate.current) {
                    const selectedItems = selectionRef.current!.getSelection() as gfileuserselect[];
                    // Map over the current fileMultiSelect to update their 'selected' status
                    const updatedFileMultiSelect = fileMultiSelect.map(f => ({
                        ...f,
                        selected: selectedItems.some(s => s.fileid === f.fileid)
                    }));
                    setFileMultiSelect(updatedFileMultiSelect);
                }
            }
        });
    }

    // Filtered file list based on search term
    const filteredFileMultiSelect =
        searchTerm.trim() === ""
            ? fileMultiSelect
            : fileMultiSelect.filter(
                  f =>
                      f.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      f.doc_category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      f.modified?.toLowerCase().includes(searchTerm.toLowerCase())
              );

    // --- Selection logic for filtered list ---
    // Use a single filteredSelectionRef, declared only once at the top
    // Remove any previous filteredSelectionRef declaration above this point
    // Use a unique name to avoid redeclaration
    const filteredSelectionRefUnique = useRef<Selection>();
    if (!filteredSelectionRefUnique.current) {
        filteredSelectionRefUnique.current = new Selection({
            selectionMode: SelectionMode.multiple,
            onSelectionChanged: () => {
                if (!isProgrammaticSelectionUpdate.current) {
                    const selectedFilteredItems = filteredSelectionRefUnique.current!.getSelection() as gfileuserselect[];
                    setFileMultiSelect(prev =>
                        prev.map(f => ({
                            ...f,
                            selected: selectedFilteredItems.some(sf => sf.fileid === f.fileid)
                        }))
                    );
                }
            }
        });
    }

    useEffect(() => {
        if (filteredSelectionRefUnique.current && filteredFileMultiSelect) {
            isProgrammaticSelectionUpdate.current = true;
            filteredFileMultiSelect.forEach((f, idx) => {
                filteredSelectionRefUnique.current!.setIndexSelected(idx, !!f.selected, false);
            });
            isProgrammaticSelectionUpdate.current = false;
        }
    }, [filteredFileMultiSelect, fileMultiSelect]);

    // Function to handle column header clicks for sorting
    const onColumnClick = useCallback(
        (event: React.MouseEvent<HTMLElement>, column: IColumn) => {
            const newSortConfig = {
                key: column.fieldName!,
                isSortedDescending: sortConfig.key === column.fieldName ? !sortConfig.isSortedDescending : false
            };
            setSortConfig(newSortConfig);

            const sortedItems = [...fileMultiSelect].sort((a, b) => {
                const aValue = a[column.fieldName as keyof gfileuserselect];
                const bValue = b[column.fieldName as keyof gfileuserselect];

                if (column.fieldName === "modified") {
                    const dateA = new Date(aValue as string);
                    const dateB = new Date(bValue as string);

                    if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) {
                        return 0;
                    }
                    if (isNaN(dateA.getTime())) {
                        return newSortConfig.isSortedDescending ? -1 : 1;
                    }
                    if (isNaN(dateB.getTime())) {
                        return newSortConfig.isSortedDescending ? 1 : -1;
                    }

                    return newSortConfig.isSortedDescending ? dateB.getTime() - dateA.getTime() : dateA.getTime() - dateB.getTime();
                }

                if (typeof aValue === "string" && typeof bValue === "string") {
                    return newSortConfig.isSortedDescending ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue);
                }

                if (aValue === bValue) {
                    return 0;
                }
                if (aValue === null || aValue === undefined) {
                    return newSortConfig.isSortedDescending ? -1 : 1;
                }
                if (bValue === null || bValue === undefined) {
                    return newSortConfig.isSortedDescending ? 1 : -1;
                }

                return newSortConfig.isSortedDescending ? (bValue < aValue ? -1 : 1) : aValue < bValue ? -1 : 1;
            });

            setFileMultiSelect(sortedItems);
        },
        [fileMultiSelect, sortConfig]
    );

    // This useEffect will manage the DetailsList selection based on fileMultiSelect
    useEffect(() => {
        if (selectionRef.current && fileMultiSelect) {
            // Set flag to indicate programmatic update
            isProgrammaticSelectionUpdate.current = true;
            fileMultiSelect.forEach((f, idx) => {
                selectionRef.current!.setIndexSelected(idx, !!f.selected, false);
            });
            // Reset flag after update
            isProgrammaticSelectionUpdate.current = false;
        }
    }, [fileMultiSelect]); // Only re-run when fileMultiSelect itself changes

    useEffect(() => {
        setIsSpeakingState(isSpeaking || false);
    }, [isSpeaking]);

    // Initial load and subsequent changes to fileselection prop
    useEffect(() => {
        if (fileselection) {
            // Ensure we set the selection based on the initial prop
            const initialFileMultiSelect = fileselection.map(f => ({ ...f }));
            setFileMultiSelect(initialFileMultiSelect);

            // Also, update the DetailsList selection after initial state set
            // This will be handled by the useEffect above with [fileMultiSelect] dependency
        }
    }, [fileselection]);

    const onInputChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setQ(newValue || "");
    };
    const setQ = (newValue: string) => {
        !newValue ? (qRef.current = "") : (qRef.current = newValue);
        adjustHeight(styles.questionInputContainer);
        forceRender();
    };
    const setConvQ = (newValue: string) => {
        convRef.current = newValue;
        adjustHeight(styles.questionInputContainer);
        forceRender();
    };
    const sendQ = () => {
        if (disabled || !qRef.current.trim()) return;
        q_sequential_ref.current++;

        onSend(
            qRef.current,
            q_sequential_ref.current,
            window.global_expected_next_user_response_type,
            ignore_discard_ref.current,
            additional_instr.current || ""
        );
        if (clearOnSend) setQ("");
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQ();
        }
    };




    const sendConv = () => {
        if (!convRef.current.trim()) return;
        q_sequential_ref.current++;
        import.meta.env.DEV && console.log("‹📤‾sendConv›", convRef.current, ignore_discard_ref.current);
        onSend(
            convRef.current,
            q_sequential_ref.current,
            intent_ref.current,
            ignore_discard_ref.current,
            additional_instr.current || "",
            msgovrride_ref.current
        );
        setConvQ("");
        if (clearOnSend) setQ("");
    };

    const simulateSendClick = () => {
        const event = new MouseEvent("click", {
            bubbles: true,
            cancelable: true,
            view: window
        });
        if (convDivRef.current) (convDivRef.current as any).dispatchEvent(event);
    };
    const handleNewConverson = () => {
        setChatControlsOpen(false);
        newConverson();
    };
    const handleNewConverson_v2 = () => {
        setChatControlsOpen(false);
        setQ("");
        newConverson();
    };
    const handleToggleLeftPanel = () => {
        setChatControlsOpen(false);
        toggleLeftPanel();
    };
    const callOutVisibility = (val: boolean) => {};

    const sendQDisabled = disabled || !qRef.current.trim();

    const isOnCallbackFromSpeech = (isOn: boolean) => {
        setIsOn(isOn);
        isOnCallback(isOn);
    };
    const handleButtonClick = () => {
        setIsFileUploadDiagOpen(true);
    };
    const onRecognizing = (text: string, language: string) => {
        setConvQ(text);
        setQ(text);
    };
    const onRecognized = (
        text: string,
        language: string,
        current_intent: string,
        is_request_for_info?: Boolean,
        ignore_discard?: boolean,
        value_valid_bot_not_in_data?: string,
        msg_ovrride?: string
    ) => {
        if (is_request_for_info && is_request_for_info == true) window.globalRequestForInfo = true;

        ignore_discard_ref.current = ignore_discard || false;
        additional_instr.current = value_valid_bot_not_in_data || "";
        intent_ref.current = current_intent || "";
        msgovrride_ref.current = msg_ovrride || "";

        SetIterQ(prev => prev + 1);
        setConvQ(text);
        setQ(text);

        setTimeout(() => {
            import.meta.env.DEV && console.log("‹🚀‾send›", text);
            simulateSendClick();
        }, 400)
    };

    // The handleFileCheckboxChange is likely not directly used for DetailsList checkboxes
    // as the selection is managed by the Selection object's onSelectionChanged.
    // However, if you have custom checkboxes in the list, keep this.
    // const handleFileCheckboxChange = (fileid: string, checked: boolean) => {
    //     setFileMultiSelect(prev => prev.map(f => (f.fileid === fileid ? { ...f, selected: checked } : f)));
    // };

    const finalizeFileSelection = () => {
        setIsFileMultiSelectCalloutOpen(false);
        // Pass a copy to handleSelectedFids if it modifies the array externally
        handleSelectedFids([...fileMultiSelect]);

        import.meta.env.DEV && console.log("‹fileMultiSelect›", fileMultiSelect);
    };

    const finalizeAllSelection = () => {
        if (selectionRef.current) {
            isProgrammaticSelectionUpdate.current = true; // Indicate programmatic update
            selectionRef.current.setAllSelected(true); // Select all items in DetailsList
            isProgrammaticSelectionUpdate.current = false; // Reset flag

            // Manually update fileMultiSelect to reflect all selected
            let fMultiSel: gfileuserselect[] = [];
            setFileMultiSelect(prev => {
                fMultiSel = prev.map(f => ({ ...f, selected: true }));
                return fMultiSel;
            });
            handleSelectedFids(fMultiSel);
        }
        setIsFileMultiSelectCalloutOpen(false);
    };

    // Filtered file list based on search term
    const filteredFileMultiSelect_v2 =
        searchTerm.trim() === ""
            ? fileMultiSelect
            : fileMultiSelect.filter(
                  f =>
                      f.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      f.doc_category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      f.modified?.toLowerCase().includes(searchTerm.toLowerCase())
              );

    // --- Selection logic for filtered list ---
    // Create a new Selection object for the filtered list
    const filteredSelectionRef = useRef<Selection>();
    if (!filteredSelectionRef.current) {
        filteredSelectionRef.current = new Selection({
            selectionMode: SelectionMode.multiple,
            onSelectionChanged: () => {
                if (!isProgrammaticSelectionUpdate.current) {
                    const selectedFilteredItems = filteredSelectionRef.current!.getSelection() as gfileuserselect[];
                    // Map selected items in filtered list to the main fileMultiSelect by fileid
                    setFileMultiSelect(prev =>
                        prev.map(f => ({
                            ...f,
                            selected: selectedFilteredItems.some(sf => sf.fileid === f.fileid)
                        }))
                    );
                }
            }
        });
    }

    // Keep filteredSelectionRef in sync with fileMultiSelect and searchTerm
    useEffect(() => {
        if (filteredSelectionRef.current && filteredFileMultiSelect_v2) {
            isProgrammaticSelectionUpdate.current = true;
            filteredFileMultiSelect_v2.forEach((f, idx) => {
                filteredSelectionRef.current!.setIndexSelected(idx, !!f.selected, false);
            });
            isProgrammaticSelectionUpdate.current = false;
        }
    }, [filteredFileMultiSelect_v2, fileMultiSelect]);

    return (
        <Stack
            horizontal
            className={`${styles.questionInputContainer} ${
                (selectedGroup.premiumby ?? "") == "" && selectedGroup.dailymsgs > (lTiers?.at(0)?.msgsplmt || 0) ? styles.noMsggsConditional : ""
            }`}
        >
            {/* File Multi-Select Callout */}
            <Callout
                gapSpace={0}
                styles={questionInputUploadCallout}
                isBeakVisible={false}
                target={fileMultiSelectButRef.current}
                onDismiss={() => setIsFileMultiSelectCalloutOpen(false)}
                setInitialFocus
                directionalHint={DirectionalHint.leftTopEdge}
                className={styles.calloutMain}
                hidden={!isFileMultiSelectCalloutOpen}
            >
                <div style={{ minWidth: 420, maxWidth: 620, display: "flex", flexDirection: "column", maxHeight: 340 }}>
                    <div style={{ flex: 1, overflowY: "auto", minHeight: 120, maxWidth: 620, width: "100%", color: "#2F4858" }}>
                        <DetailsList
                            items={filteredFileMultiSelect}
                            columns={fileColumns.map(column => ({
                                ...column,
                                isSorted: sortConfig.key === column.fieldName,
                                isSortedDescending: sortConfig.key === column.fieldName ? sortConfig.isSortedDescending : false
                            }))}
                            selection={filteredSelectionRefUnique.current}
                            selectionMode={SelectionMode.multiple}
                            setKey="fileMultiSelectList"
                            checkboxVisibility={1} // on for selection
                            compact={true}
                            layoutMode={DetailsListLayoutMode.justified}
                            onRenderDetailsHeader={(props?: IDetailsHeaderProps) => onRenderDetailsHeader(props!)}
                            styles={{
                                root: { minHeight: 120, color: "black" }, // whole component text color
                                headerWrapper: { color: "black" },
                                contentWrapper: { color: "black" }
                            }}
                            onItemInvoked={(item, index, event) => {
                                onCitationClicked(`${item.name}#page=1_fid=${item.fileid}`, null);
                            }}
                        />
                    </div>
                    {/* Search Field */}
                    <TextField placeholder="Search..." value={searchTerm} onChange={(_e, v) => setSearchTerm(v || "")} styles={searchTextFieldInCalloutStyle} />
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: 8,
                            borderTop: "1px solid #eee",
                            background: "#fff",
                            position: "sticky",
                            bottom: 0,
                            zIndex: 1
                        }}
                    >
                        <PrimaryButton
                            style={{ width: "70%", borderRadius: "5px" }}
                            onClick={finalizeFileSelection}
                            disabled={fileMultiSelect.filter(f => f.selected).length === 0}
                        >
                            Select
                        </PrimaryButton>
                        <PrimaryButton style={{ width: "10%", marginLeft: "10px", borderRadius: "5px" }} onClick={finalizeAllSelection}>
                            All
                        </PrimaryButton>
                    </div>
                </div>
            </Callout>
            {/* Upload Callout */}
            <Callout
                gapSpace={0}
                styles={questionInputUploadCallout}
                isBeakVisible={false}
                target={uploadButRef.current}
                onDismiss={() => setIsFileUploadDiagOpen(false)}
                setInitialFocus
                directionalHint={DirectionalHint.leftTopEdge}
                className={styles.calloutMain}
                hidden={!isFileUploadDiagOpen}
            >
                <div className={styles.rightButtons}>
                    <div className={styles.libButs} id={`calloutButton_pdf`}>
                        <TooltipHost
                            content="Upload Documents"
                            directionalHint={DirectionalHint.topCenter}
                            styles={tooltipStylesThumbInScopeChooser}
                            className={styles.tooltipClass}
                        >
                            {/* Upload PDFs Button */}
                            <AddURLs
                                className={styles.commandButtonLib}
                                updateCatsState={updateCatsState}
                                butName="Documents"
                                calloutLabelText="Upload PDFs from your Device"
                                cTy="pdf"
                                listRefreshRequest={listRefreshRequest}
                                selectedGroup={selectedGroup}
                                callOutVisibility={callOutVisibility}
                                hideCallout={isFileUploadDiagOpen == false ? true : false}
                                isfromchat={true}
                            />
                        </TooltipHost>
                    </div>
                    <div className={styles.libButs} id={`calloutButton_img`}>
                        <TooltipHost
                            content="Upload Images"
                            directionalHint={DirectionalHint.topCenter}
                            styles={tooltipStylesThumbInScopeChooser}
                            className={styles.tooltipClass}
                        >
                            {/* Upload PDFs Button */}
                            <AddURLs
                                className={styles.commandButtonLib}
                                updateCatsState={updateCatsState}
                                butName="Images"
                                calloutLabelText="Upload Images from your Device"
                                cTy="pdf"
                                listRefreshRequest={listRefreshRequest}
                                selectedGroup={selectedGroup}
                                callOutVisibility={callOutVisibility}
                                hideCallout={isFileUploadDiagOpen == false ? true : false}
                                acceptStr="image/*"
                                isfromchat={true}
                            />
                        </TooltipHost>
                    </div>
                    {selectedGroup.allowvideofiles == 1 && (
                        <div className={styles.libButs} id={`calloutButton_vid`}>
                            <TooltipHost content="Add Video Files" directionalHint={DirectionalHint.topCenter}>
                                {/* Upload Video Button */}
                                <AddURLs
                                    className={styles.commandButtonLib}
                                    updateCatsState={(newValue: string) => updateCatsState(newValue)}
                                    butName="Video"
                                    calloutLabelText="Upload Video Files from your Device"
                                    cTy="vid"
                                    listRefreshRequest={listRefreshRequest}
                                    selectedGroup={selectedGroup}
                                    callOutVisibility={callOutVisibility}
                                    isfromchat={true}
                                />
                            </TooltipHost>
                        </div>
                    )}
                    {selectedGroup.allowyoutube == 1 && (
                        <div className={styles.libButs} id={`calloutButton_youtube`}>
                            <TooltipHost content="Add YouTube videos" directionalHint={DirectionalHint.topCenter}>
                                {/* Upload Youtube Button */}
                                <AddURLs
                                    className={styles.commandButtonLib}
                                    updateCatsState={updateCatsState}
                                    butName="YouTube"
                                    calloutLabelText="Enter YouTube URL"
                                    cTy="youtube"
                                    listRefreshRequest={listRefreshRequest}
                                    selectedGroup={selectedGroup}
                                    callOutVisibility={callOutVisibility}
                                    hideCallout={isFileUploadDiagOpen ? false : true}
                                    isfromchat={true}
                                />
                            </TooltipHost>
                        </div>
                    )}
                    {selectedGroup.allowweb == 1 && (
                        <div className={styles.libButs} id={`calloutButton_web`}>
                            <TooltipHost content="Add Web Pages" directionalHint={DirectionalHint.topCenter}>
                                {/* Upload Web Button */}
                                <AddURLs
                                    className={styles.commandButtonLib}
                                    updateCatsState={updateCatsState}
                                    butName="Web"
                                    calloutLabelText="Enter Web URL"
                                    cTy="web"
                                    listRefreshRequest={listRefreshRequest}
                                    selectedGroup={selectedGroup}
                                    callOutVisibility={callOutVisibility}
                                    hideCallout={isFileUploadDiagOpen ? false : true}
                                    isfromchat={true}
                                />
                            </TooltipHost>
                        </div>
                    )}
                    {selectedGroup.allowaudiodocentry == 1 && (
                        <div className={styles.libButs} id={`calloutButton_mic`}>
                            {/* Record Audio Button */}
                            <TooltipHost content="Record Content" directionalHint={DirectionalHint.topCenter}>
                                <AddURLs
                                    className={styles.commandButtonLib}
                                    updateCatsState={updateCatsState}
                                    butName="Mic"
                                    calloutLabelText="Record Audio"
                                    cTy="mic"
                                    listRefreshRequest={listRefreshRequest}
                                    selectedGroup={selectedGroup}
                                    callOutVisibility={callOutVisibility}
                                    isfromchat={true}
                                />
                            </TooltipHost>
                        </div>
                    )}
                </div>
            </Callout>
            {/* NOT USED. Chat Controls Slide Out */}

            {chatControlsOpen && (
                <ChatControls
                    isOpen={chatControlsOpen}
                    onDismiss={() => setChatControlsOpen(false)}
                    ButtonRef={textAreaRef}
                    toggleLeftPanel={handleToggleLeftPanel}
                    newConverson={handleNewConverson}
                />
            )}
            {/* New Convo / Convo History Button Container */}
            <div ref={textAreaRef} className={styles.chatControlsContainer}>
                {/*New Conversation Button */}
                <ClearChatButton className={styles.commandButtonTwo} onClick={handleNewConverson_v2} />
                {/* Show Conversation History Button */}
                <TogglePanelButton
                    className={styles.commandButton}
                    onClick={() => handleToggleLeftPanel()}
                    text=""
                    iconType={List28Regular}
                    tooltipText="View your Conversation History"
                />
            </div>
            {/* Speech Button */}
            {selectedGroup.allowsimpleconvo > 0 && (
                <SpeechRecognizer
                    onRecognized={onRecognized}
                    onRecognizing={onRecognizing}
                    isOnCallback={isOnCallbackFromSpeech}
                    convoEnd={convoEnd}
                    selectedGroup={selectedGroup}
                    isSpeaking={isSpeakingState}
                    isSpeechPaused={isSpeechPaused}
                    lastMessage={lastMessage}
                    activeConvoKey={activeConvoKey}
                    question_and_answers_list={question_and_answers_list}
                    convo_discard_stamp={convo_discard_stamp}
                />
            )}
            <TextField
                id={"kgnfqinputfield"}
                className={styles.questionInputTextArea}
                styles={questionInputTextArea}
                autoAdjustHeight={true}
                placeholder={(selectedGroup.premiumby ?? '') == '' && selectedGroup.dailymsgs > (lTiers?.at(0)?.msgsplmt || 0) ? msgLmtHit : placeholder}
                multiline
                disabled={(selectedGroup.premiumby ?? '') == '' && selectedGroup.dailymsgs > (lTiers?.at(0)?.msgsplmt || 0) ? true : false}
                resizable={false}
                borderless
                value={qRef.current}
                onChange={onInputChange}
                onKeyDown={onEnterPress}
            />
            {/* Show Upload Callout Button */}
            {/* File Multi-Select Callout Button */}

            <div ref={fileMultiSelectButRef} className={styles.questionInputButtonsContainer}>
                <TooltipHost content="Set which files to include in the conversation" directionalHint={DirectionalHint.topCenter}>
                    <ActionButton
                        className={styles.btn_action}
                        id="fileMultiSelectCalloutButton"
                        iconProps={{ iconName: undefined }}
                        styles={actionButtonStyles}
                        allowDisabledFocus
                        onClick={() => setIsFileMultiSelectCalloutOpen(true)}
                    >
                        <GoMultiSelect size={22} />
                    </ActionButton>
                </TooltipHost>
            </div>
            <div ref={uploadButRef} className={styles.questionInputButtonsContainerRight}>
                {permissionLevel > 1 && (
                    <TooltipHost content="Upload" directionalHint={DirectionalHint.topCenter}>
                        <ActionButton
                            className={styles.btn_action}
                            id="calloutButton"
                            iconProps={clipIcon}
                            styles={actionButtonStyles}
                            allowDisabledFocus
                            onClick={handleButtonClick}
                        ></ActionButton>
                    </TooltipHost>
                )}
            </div>
            {/* Send Message / Stop Streaming Button */}

            <div className={styles.questionInputButtonsContainer} ref={butContainerRef} key={`akg_${isOn}`}>
                {isStreaming == true ? (
                    <Button
                        size="large"
                        className={styles.sendButton}
                        icon={<Stop24Filled primaryFill="rgba(255, 255, 255, 0.6)" />}
                        onClick={isCancellingMidway}
                        style={{ backgroundColor: "#F1641E", borderRadius: "5px" }}
                    />
                ) : (
                    <Button
                        size="large"
                        id={"kgnfqsendbutton"}
                        ref={sendButRef}
                        className={styles.sendButton}
                        icon={<Send28Filled primaryFill="rgba(255, 255, 255, 0.6)" />}
                        disabled={sendQDisabled}
                        onClick={sendQ}
                        style={{ backgroundColor: "#F1641E", borderRadius: "5px" }}
                    />
                )}
                <div id={"kgnfqsendtarget"} className={styles.kgnfsndtrgt} onClick={sendConv} ref={convDivRef}></div>
            </div>
        </Stack>
    );
};
